import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  Download,
  Edit2,
  Save,
  X,
  FileText,
  Shield,
  Inbox,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { zugferdAPI, downloadFile } from '../../services/api';
import { formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import './IncomingInvoices.css';

const STATUS_LABELS = {
  pending: 'Prüfung',
  verified: 'Geprüft',
  booked: 'Gebucht',
  paid: 'Bezahlt',
  rejected: 'Abgelehnt',
};
const STATUS_CLASS = {
  pending: 'status-badge status-draft',
  verified: 'status-badge status-sent',
  booked: 'status-badge status-sent',
  paid: 'status-badge status-paid',
  rejected: 'status-badge status-overdue',
};
const SOURCE_LABELS = {
  zugferd: 'ZUGFeRD XML',
  ai: 'KI-Erkennung',
  manual: 'Manuell',
};

const formatDE = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE');
};

const IncomingInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const loadList = async () => {
    try {
      setLoading(true);
      const { data } = await zugferdAPI.listIncoming({
        status: statusFilter || undefined,
        limit: 200,
      });
      setInvoices(data);
    } catch {
      toast.error('Fehler beim Laden der Eingangsrechnungen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'xml'].includes(ext)) {
      toast.error('Nur PDF oder XML Dateien');
      return;
    }
    try {
      setUploading(true);
      const { data } = await zugferdAPI.receive(file);
      toast.success(
        data.source === 'zugferd'
          ? `ZUGFeRD-Rechnung von ${data.supplier_name} erkannt`
          : data.source === 'xrechnung'
          ? `XRechnung von ${data.supplier_name} erkannt`
          : `Rechnung per KI erkannt: ${data.supplier_name}`
      );
      if (fileRef.current) fileRef.current.value = '';
      loadList();
      setSelectedId(data.id);
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (inv) => {
    setSelectedId(inv.id);
    setEditing(false);
    try {
      const { data } = await zugferdAPI.getIncoming(inv.id);
      setDetail(data);
    } catch {
      toast.error('Fehler beim Laden');
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setEditing(false);
  };

  const handleDownloadPdf = async () => {
    if (!detail?.pdf_path) {
      toast.error('Kein PDF vorhanden');
      return;
    }
    try {
      const res = await fetch(`/api/zugferd/incoming/${detail.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      downloadFile(blob, detail.original_filename || `incoming_${detail.id}.pdf`);
    } catch {
      toast.error('Download fehlgeschlagen');
    }
  };

  const startEdit = () => {
    setEditForm({
      invoice_number: detail.invoice_number || '',
      invoice_date: detail.invoice_date || '',
      due_date: detail.due_date || '',
      supplier_name: detail.supplier_name || '',
      net_amount: detail.net_amount || '',
      vat_amount: detail.vat_amount || '',
      gross_amount: detail.gross_amount || '',
      notes: detail.notes || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await zugferdAPI.updateIncoming(detail.id, editForm);
      setDetail(data);
      setEditing(false);
      toast.success('Gespeichert');
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const labels = { verified: 'geprüft', paid: 'bezahlt', rejected: 'abgelehnt' };
    if (!window.confirm(`Rechnung als "${labels[newStatus]}" markieren?`)) return;
    try {
      const { data } = await zugferdAPI.updateIncoming(detail.id, { status: newStatus });
      setDetail(data);
      toast.success(`Status: ${STATUS_LABELS[newStatus]}`);
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Eingangsrechnungen</h1>
          <p>ZUGFeRD-Rechnungen empfangen, prüfen und verbuchen</p>
        </div>
        <div className="flex gap-2">
          <label className={`btn btn-primary ${uploading ? 'opacity-50' : ''}`}>
            <Upload size={16} />
            {uploading ? 'Wird verarbeitet...' : 'Rechnung hochladen'}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.xml"
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      <div className="content-body">
        {/* Filters */}
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex gap-4 items-center">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="">Alle Status</option>
                <option value="pending">Prüfung</option>
                <option value="verified">Geprüft</option>
                <option value="booked">Gebucht</option>
                <option value="paid">Bezahlt</option>
                <option value="rejected">Abgelehnt</option>
              </select>
              <span className="text-muted">
                {invoices.length} Rechnung{invoices.length !== 1 ? 'en' : ''}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="large" /></div>
        ) : invoices.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <Inbox size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">Keine Eingangsrechnungen</h3>
              <p className="text-gray-600 mb-4">
                Laden Sie eine PDF- oder XML-Rechnung hoch. ZUGFeRD-Daten werden
                automatisch extrahiert, andere PDFs per KI erkannt.
              </p>
            </div>
          </div>
        ) : (
          <div className="incoming-layout">
            {/* List */}
            <div className="incoming-list">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  className={`incoming-row ${selectedId === inv.id ? 'active' : ''}`}
                  onClick={() => openDetail(inv)}
                >
                  <div className="incoming-row-top">
                    <span className="font-semibold">{inv.supplier_name}</span>
                    <span className={STATUS_CLASS[inv.status]}>
                      {STATUS_LABELS[inv.status]}
                    </span>
                  </div>
                  <div className="incoming-row-mid">
                    <span>{inv.invoice_number || 'Ohne Nr.'}</span>
                    <span className="font-semibold">{formatEuro(inv.gross_amount)}</span>
                  </div>
                  <div className="incoming-row-bot">
                    <span className="text-muted">{formatDE(inv.invoice_date)}</span>
                    <span className={`source-badge source-${inv.source}`}>
                      {SOURCE_LABELS[inv.source] || inv.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Detail panel */}
            <div className="incoming-detail">
              {detail ? (
                <div className="card">
                  <div className="card-header company-header-row">
                    <h2>{detail.supplier_name}</h2>
                    <div className="flex gap-2">
                      {!editing && (
                        <button className="btn btn-outline btn-sm" onClick={startEdit}>
                          <Edit2 size={14} /> Korrigieren
                        </button>
                      )}
                      {detail.pdf_path && (
                        <button className="btn btn-outline btn-sm" onClick={handleDownloadPdf}>
                          <Download size={14} /> PDF
                        </button>
                      )}
                      <button className="btn btn-outline btn-sm" onClick={closeDetail}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {!editing ? (
                      <>
                        <dl className="invoice-meta mb-4">
                          <dt>Rechnungsnr.</dt><dd>{detail.invoice_number || '—'}</dd>
                          <dt>Datum</dt><dd>{formatDE(detail.invoice_date)}</dd>
                          <dt>Fällig</dt><dd>{formatDE(detail.due_date)}</dd>
                          <dt>Netto</dt><dd>{formatEuro(detail.net_amount)}</dd>
                          <dt>USt</dt><dd>{formatEuro(detail.vat_amount)}</dd>
                          <dt>Brutto</dt>
                          <dd className="font-semibold" style={{ color: '#1d4ed8' }}>
                            {formatEuro(detail.gross_amount)}
                          </dd>
                          <dt>Währung</dt><dd>{detail.currency}</dd>
                          <dt>Quelle</dt>
                          <dd>
                            <span className={`source-badge source-${detail.source}`}>
                              {SOURCE_LABELS[detail.source]}
                            </span>
                            {detail.source === 'ai' && (
                              <span className="confidence-badge ml-2">
                                {Math.round(detail.confidence * 100)}% sicher
                              </span>
                            )}
                          </dd>
                          <dt>Status</dt>
                          <dd>
                            <span className={STATUS_CLASS[detail.status]}>
                              {STATUS_LABELS[detail.status]}
                            </span>
                          </dd>
                          {detail.original_filename && (
                            <><dt>Datei</dt><dd>{detail.original_filename}</dd></>
                          )}
                          {detail.notes && (
                            <><dt>Notizen</dt><dd>{detail.notes}</dd></>
                          )}
                        </dl>

                        {/* Line items */}
                        {detail.items_json && detail.items_json.length > 0 && (
                          <div className="mb-4">
                            <h3 className="text-sm font-semibold mb-2">Positionen</h3>
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Pos.</th>
                                  <th>Beschreibung</th>
                                  <th className="text-right">Menge</th>
                                  <th className="text-right">Einzelpreis</th>
                                  <th className="text-right">Netto</th>
                                  <th className="text-right">USt%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.items_json.map((it, i) => (
                                  <tr key={i}>
                                    <td>{it.position || i + 1}</td>
                                    <td>{it.description}</td>
                                    <td className="text-right">{it.quantity}</td>
                                    <td className="text-right">{formatEuro(it.unit_price)}</td>
                                    <td className="text-right">{formatEuro(it.net_amount)}</td>
                                    <td className="text-right">{it.vat_rate}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Warnings */}
                        {detail.warnings && detail.warnings.length > 0 && (
                          <div className="incoming-warnings mb-4">
                            <strong>Hinweise:</strong>
                            <ul>
                              {detail.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Status actions */}
                        <div className="incoming-actions">
                          {detail.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleStatusChange('verified')}
                              >
                                <CheckCircle2 size={14} /> Als geprüft markieren
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleStatusChange('rejected')}
                              >
                                <XCircle size={14} /> Ablehnen
                              </button>
                            </>
                          )}
                          {detail.status === 'verified' && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleStatusChange('paid')}
                            >
                              <CheckCircle2 size={14} /> Als bezahlt markieren
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Edit form */
                      <div className="incoming-edit-form">
                        {[
                          { key: 'supplier_name', label: 'Lieferant' },
                          { key: 'invoice_number', label: 'Rechnungsnr.' },
                          { key: 'invoice_date', label: 'Datum', type: 'date' },
                          { key: 'due_date', label: 'Fällig', type: 'date' },
                          { key: 'net_amount', label: 'Netto', type: 'number' },
                          { key: 'vat_amount', label: 'USt', type: 'number' },
                          { key: 'gross_amount', label: 'Brutto', type: 'number' },
                        ].map(({ key, label, type }) => (
                          <div className="form-group" key={key}>
                            <label className="form-label">{label}</label>
                            <input
                              type={type || 'text'}
                              className="form-input"
                              value={editForm[key] || ''}
                              onChange={(e) =>
                                setEditForm((p) => ({ ...p, [key]: e.target.value }))
                              }
                              step={type === 'number' ? '0.01' : undefined}
                            />
                          </div>
                        ))}
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                          <label className="form-label">Notizen</label>
                          <textarea
                            className="form-input"
                            value={editForm.notes || ''}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, notes: e.target.value }))
                            }
                            rows={2}
                          />
                        </div>
                        <div className="flex gap-2" style={{ gridColumn: '1 / -1' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSave}
                            disabled={saving}
                          >
                            <Save size={14} /> {saving ? 'Speichert...' : 'Speichern'}
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => setEditing(false)}
                            disabled={saving}
                          >
                            <X size={14} /> Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body text-center py-12 text-muted">
                    <Eye size={32} className="mx-auto mb-3 text-gray-300" />
                    <p>Rechnung auswählen, um Details zu sehen</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomingInvoices;
